import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, RefreshCw, ShieldCheck } from "lucide-react";
import { API, authHeader, apiGet, apiPut } from "@/lib/api";
import { AdminMonitor } from "@/components/AdminMonitor";
import { NewsManager } from "@/components/NewsManager";
import { AdminSupport } from "@/components/terminal/AdminSupport";
import { AdminPayments } from "@/components/AdminPayments";

const WaitlistPanel = () => {
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/waitlist`, { headers: authHeader() });
    setRows(data.items);
  }, []);

  useEffect(() => { load().catch(() => toast.error("Gagal memuat waitlist")); }, [load]);

  const downloadCsv = async () => {
    const { data } = await axios.get(`${API}/admin/waitlist/export`, {
      headers: authHeader(),
      responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "waitlist.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="waitlist-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-xs text-mute-dim" data-testid="waitlist-count">
          &gt; total pendaftar: {rows ? rows.length : "..."}
        </p>
        <div className="flex gap-2">
          <button onClick={load} data-testid="waitlist-refresh"
            className="flex items-center gap-2 border border-white/20 px-3 py-1.5 font-mono text-[10px] text-mute transition-colors hover:text-white">
            <RefreshCw size={12} /> MUAT ULANG
          </button>
          <button onClick={downloadCsv} data-testid="waitlist-export"
            className="flex items-center gap-2 border border-buy bg-buy/10 px-3 py-1.5 font-mono text-[10px] font-bold text-buy transition-colors hover:bg-buy hover:text-ink">
            <Download size={12} /> UNDUH CSV
          </button>
        </div>
      </div>
      <div className="mt-3 overflow-x-auto border border-white/10">
        <table className="w-full min-w-[480px] text-left" data-testid="waitlist-table">
          <thead>
            <tr className="border-b border-white/10 bg-ink-surface">
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-mute-dim">#</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-mute-dim">Email</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-mute-dim">Terdaftar</th>
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              <tr><td colSpan={3} className="px-4 py-8 text-center font-mono text-xs text-mute-dim">memuat...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={3} className="px-4 py-8 text-center font-mono text-xs text-mute-dim">belum ada pendaftar</td></tr>
            ) : (
              rows.map((r, i) => (
                <tr key={r.id} className="border-b border-white/5 transition-colors hover:bg-white/[0.03]" data-testid={`waitlist-row-${i}`}>
                  <td className="px-4 py-3 font-mono text-xs text-mute-dim">{rows.length - i}</td>
                  <td className="px-4 py-3 font-mono text-sm text-white">{r.email}</td>
                  <td className="px-4 py-3 font-mono text-xs text-mute">{new Date(r.created_at).toLocaleString("id-ID")}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const BrandingPanel = () => {
  const [branding, setBranding] = useState({
    logo_url: "",
    brand_name: "NAKED CHART AI",
    tagline: "Read the market. Not the indicators",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet("/branding")
      .then((r) => setBranding(r.data))
      .catch(() => toast.error("Gagal memuat branding"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await apiPut("/admin/branding", branding);
      setBranding(data);
      toast.success("Branding berhasil disimpan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan branding");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-white/10 bg-[#0d1219] p-4" data-testid="branding-panel">
      <p className="font-mono text-xs font-bold uppercase tracking-widest text-white">
        Custom Branding
      </p>
      <p className="mt-1 font-mono text-[10px] text-mute-dim">
        Atur logo dan identitas yang tampil di terminal.
      </p>

      <div className="mt-4 space-y-3">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-mute-dim">
            Logo URL
          </label>
          <input
            value={branding.logo_url || ""}
            onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
            placeholder="https://..."
            className="mt-1 h-9 w-full border border-white/15 bg-ink px-3 font-mono text-xs outline-none focus:border-buy/60"
          />
        </div>

        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-mute-dim">
            Nama Brand
          </label>
          <input
            value={branding.brand_name || ""}
            onChange={(e) => setBranding({ ...branding, brand_name: e.target.value })}
            className="mt-1 h-9 w-full border border-white/15 bg-ink px-3 font-mono text-xs outline-none focus:border-buy/60"
          />
        </div>

        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-mute-dim">
            Tagline
          </label>
          <input
            value={branding.tagline || ""}
            onChange={(e) => setBranding({ ...branding, tagline: e.target.value })}
            className="mt-1 h-9 w-full border border-white/15 bg-ink px-3 font-mono text-xs outline-none focus:border-buy/60"
          />
        </div>

        {branding.logo_url && (
          <div className="border border-white/10 bg-black/20 p-3">
            <p className="mb-2 font-mono text-[9px] uppercase tracking-widest text-mute-dim">
              Preview
            </p>
            <img
              src={branding.logo_url}
              alt="Logo preview"
              className="h-12 max-w-[220px] object-contain"
            />
          </div>
        )}

        <button
          onClick={save}
          disabled={saving}
          className="h-9 border border-buy bg-buy/10 px-4 font-mono text-[10px] font-bold uppercase tracking-widest text-buy transition-colors hover:bg-buy hover:text-ink disabled:opacity-50"
        >
          {saving ? "MENYIMPAN..." : "SIMPAN BRANDING"}
        </button>
      </div>
    </div>
  );
};
const LEARNING_SIM_KEY = "nakedchart_learning_simulation";

function setLearningSimulation(state) {
  const payload = { state, ts: Date.now() };
  localStorage.setItem(LEARNING_SIM_KEY, JSON.stringify(payload));
  window.dispatchEvent(new CustomEvent("nakedchart-learning-simulation", { detail: { state } }));
}

const LearningSimulationPanel = () => (
  <div className="border border-white/10 bg-[#0d1219] p-5" data-testid="learning-simulation-panel">
    <div className="mb-5">
      <p className="font-display text-lg font-bold text-white">🤖 Learning Robot Simulation</p>
      <p className="mt-1 font-mono text-[10px] text-mute-dim">Khusus admin — hanya menguji animasi Learning. Tidak memengaruhi trading.</p>
    </div>
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {[["idle","💤","NO SIGNAL"],["good","🟢","MARKET BAGUS"],["caution","🟡","CAUTION"],["danger","🔴","HIGH RISK"],["win","🏆","WIN"],["loss","😵","LOSS"],["thinking","🧠","LEARNING"],["avoid","⛔","AVOID"]].map(([state,icon,label]) => (
        <button key={state} type="button" onClick={() => setLearningSimulation(state)} data-testid={`learning-simulation-${state}`} className="flex min-h-[82px] flex-col items-center justify-center gap-1 border border-white/10 bg-black/20 px-3 py-3 font-mono text-xs font-bold text-white transition-all duration-200 hover:border-buy/50 hover:bg-buy/10 active:scale-95">
          <span className="text-2xl">{icon}</span>
          <span>{label}</span>
        </button>
      ))}
    </div>
    <button type="button" onClick={() => setLearningSimulation("idle")} data-testid="learning-simulation-reset" className="mt-4 border border-white/15 px-4 py-2 font-mono text-[10px] text-mute transition-colors hover:border-white/30 hover:text-white">
      RESET SIMULATION
    </button>
  </div>
);

const SUBTABS = [
  ["monitor", "Monitoring"],
  ["pembayaran", "Pembayaran"],
  ["waitlist", "Waitlist"],
  ["kalender", "News / Custom"],
  ["support", "Support"],
  ["branding", "Branding"],
  ["simulation", "🤖 Simulation"],
];

export const AdminTab = () => {
  const [sub, setSub] = useState("monitor");
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let alive = true;
    const poll = () => axios.get(`${API}/admin/support/unread`, { headers: authHeader() })
      .then((r) => alive && setUnread(r.data.unread)).catch(() => {});
    poll();
    const t = setInterval(poll, 15000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <div className="space-y-4" data-testid="admin-tab-panel">
      <div className="flex items-center gap-2 border border-brand/30 bg-brand/5 px-4 py-3">
        <ShieldCheck size={14} className="shrink-0 text-[#8b97f5]" />
        <p className="font-mono text-[10px] uppercase tracking-widest text-[#8b97f5]">
          Area khusus admin — tidak terlihat oleh user biasa
        </p>
      </div>
      <div className="flex w-full min-w-0 flex-wrap gap-px border border-white/10 bg-white/10 [@media(orientation:landscape)]:flex-nowrap" data-testid="admin-subtabs">
        {SUBTABS.map(([key, label]) => (
          <button key={key} onClick={() => setSub(key)} data-testid={`admin-subtab-${key}`}
            className={`flex-[1_0_32%] min-w-0 whitespace-nowrap px-4 py-2.5 font-mono [@media(orientation:landscape)]:flex-1 text-[11px] font-bold uppercase tracking-widest transition-colors ${
              sub === key ? "bg-buy/15 text-buy" : "bg-[#0d1219] text-mute hover:text-white"
            }`}>
            {label}
            {key === "support" && unread > 0 && (
              <span data-testid="admin-support-unread"
                className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-sell px-1 font-mono text-[8px] font-bold text-white">
                {unread}
              </span>
            )}
          </button>
        ))}
      </div>
      {sub === "monitor" && <AdminMonitor />}
      {sub === "pembayaran" && <AdminPayments />}
      {sub === "waitlist" && <WaitlistPanel />}
      {sub === "kalender" && <NewsManager />}
      {sub === "support" && <AdminSupport />}
      {sub === "branding" && <BrandingPanel />}
      {sub === "simulation" && <LearningSimulationPanel />}
    </div>
  );
};
